// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Turning a bag of positioned text fragments back into readable prose.
 *
 * NO FIXED SEPARATOR IS CORRECT, and that is not a matter of taste — it was
 * measured, on two producers, with mutually exclusive results:
 *
 *   - On pdf-lib output, `items.map(i => i.str).join('')` fuses the last word of
 *     each line into the first word of the next: "…in the widest sense.geometrical
 *     powers of increase…". Zero planted quotations were found.
 *   - On Quartz/cupsfilter output, pdf.js emits ONE ITEM PER WORD and splits the
 *     word "the" into three items — "t", ""(hasEOL), "he" — so joining on a
 *     space, or joining on hasEOL, produces "varieties of t he same species".
 *     Zero planted quotations were found.
 *
 * So the separator is derived from geometry: a space goes between two fragments
 * when the horizontal gap between them is wide enough to BE a space, measured
 * against the font size on that line. Both cases above then come out right.
 *
 * HYPHENATION FALLS OUT FOR FREE, and this is worth stating because it is the
 * single most common reason searching a PDF fails. A line ending in a hyphen
 * whose successor starts lower-case is joined with NO space and the hyphen KEPT:
 * "sub-" + "stantial" becomes "sub-stantial". Nobody can tell from the bytes
 * whether that hyphen was the author's or the typesetter's — and with hyphens
 * treated as intra-word joiners by the tokeniser, nobody has to: "sub-stantial",
 * "substantial" and "well-known" all tokenise correctly, and the text shown to
 * the reader still contains the real characters.
 */

export interface PositionedItem {
  str: string;
  /** Device-space left edge, top-left origin. */
  x: number;
  /** Device-space baseline, top-left origin. */
  y: number;
  /** Advance width in device space. */
  width: number;
  /** Glyph height in device space. 0 marks a synthesised spacer. */
  height: number;
}

export interface Line {
  text: string;
  y: number;
  /** Left edge of the line. */
  x: number;
}

/**
 * Drop the fragments pdf.js synthesises to bridge gaps.
 *
 * On a real arXiv paper 930 of 2,696 items (34%) are zero-height `" "` or `""`
 * placeholders. They carry no glyphs, so their geometry cannot be trusted for
 * line clustering, and their text is decided by the same heuristic we are trying
 * to replace. The horizontal gap they were standing in for is still visible in
 * the surrounding items' coordinates, which is where the space comes from now.
 */
export function dropPhantoms(items: PositionedItem[]): PositionedItem[] {
  return items.filter((it) => it.str.length > 0 && (it.height > 0 || it.str.trim().length > 0));
}

/**
 * Group items into visual lines by baseline. Each group is one row of the page,
 * in the order the rows appear down the page; items within a row are unsorted.
 *
 * Shared by groupLines and findGutter so that "a row" means the same thing to
 * both — the gutter is decided by counting ROWS that have ink in a bin, and that
 * count is only meaningful if the rows are the ones the assembler will use.
 */
function groupRows(items: PositionedItem[]): PositionedItem[][] {
  if (items.length === 0) return [];
  const heights = items.map((i) => i.height).filter((h) => h > 0).sort((a, b) => a - b);
  const medianHeight = heights.length > 0 ? heights[Math.floor(heights.length / 2)] : 10;
  const tolerance = Math.max(1.5, medianHeight * 0.5);

  const sorted = [...items].sort((a, b) => a.y - b.y || a.x - b.x);
  const groups: PositionedItem[][] = [];
  let current: PositionedItem[] = [sorted[0]];
  let currentY = sorted[0].y;
  for (let i = 1; i < sorted.length; i++) {
    if (Math.abs(sorted[i].y - currentY) <= tolerance) {
      current.push(sorted[i]);
    } else {
      groups.push(current);
      current = [sorted[i]];
      currentY = sorted[i].y;
    }
  }
  groups.push(current);
  return groups;
}

/**
 * The share of a page's rows that may cross the gutter and it still be a gutter.
 *
 * THIS IS THE FIX FOR THE WORST BUG THIS FILE HAS HAD, and it must not be
 * tightened back to zero. The first version marked a bin occupied if ANY item
 * overlapped it and demanded a run of COMPLETELY empty bins. One full-width item
 * — the title and abstract block on page 1 of essentially every journal article,
 * a spanning figure, a footnote rule, a centred running head — therefore
 * saturated the middle bins, findGutter returned null, and pageLines fell back
 * to reading the whole page by baseline: left and right columns merged into one
 * line where they share a baseline grid, and interleaved L1/R1/L2/R2 where they
 * do not. Measured on a real two-column A4 page built with pdf-lib and parsed
 * through pdf.js: without the title, gutter = 270.7 and the planted quotation
 * classified `exact`; with one extra full-width title line, gutter = null and
 * the same quotation classified `not-found`. Every multi-line quotation on such
 * a page became a false accusation of fabrication.
 *
 * So a gutter bin is now one that FEW rows cross rather than one no item
 * touches. 15% is enough for a title block, an abstract, a spanning figure and
 * its caption on the same page, and far too little for a single-column page,
 * where every row crosses the middle.
 */
const GUTTER_CROSS_SHARE = 0.15;

/**
 * Find the single vertical gutter of a two-column page, if there is one.
 *
 * Returns the x coordinate to split at, or null for a single-column page.
 *
 * A sibling tool measured a naive column detector finding zero columns on a
 * genuinely two-column paper and up to sixteen on table pages, so this one is
 * deliberately hard to satisfy: the gutter must be a run of bins crossed by
 * almost no rows, at least 4% of the page wide, inside the middle 60% of the
 * page, and BOTH resulting columns must then hold at least a quarter of the
 * page's characters and at least five lines. A table's white space between
 * columns is usually narrower than that, and a sparse page fails the character
 * test.
 */
export function findGutter(items: PositionedItem[], pageWidth: number): number | null {
  if (items.length < 40) return null;
  const BINS = 100;
  const rows = groupRows(items.filter((it) => it.str.trim().length > 0));
  if (rows.length === 0) return null;

  // How many ROWS have ink in each bin. Counting rows rather than items is what
  // makes one full-width line cost a bin 1 instead of disqualifying it.
  const crossings = new Uint16Array(BINS);
  for (const row of rows) {
    const touched = new Set<number>();
    for (const it of row) {
      const from = Math.max(0, Math.floor((it.x / pageWidth) * BINS));
      const to = Math.min(BINS - 1, Math.floor(((it.x + Math.max(it.width, 1)) / pageWidth) * BINS));
      for (let b = from; b <= to; b++) touched.add(b);
    }
    for (const b of touched) crossings[b] += 1;
  }
  const allowed = Math.floor(rows.length * GUTTER_CROSS_SHARE);

  const lo = Math.floor(BINS * 0.2);
  const hi = Math.ceil(BINS * 0.8);
  let bestStart = -1;
  let bestLen = 0;
  let runStart = -1;
  for (let b = lo; b <= hi; b++) {
    if (crossings[b] <= allowed) {
      if (runStart === -1) runStart = b;
      const len = b - runStart + 1;
      if (len > bestLen) {
        bestLen = len;
        bestStart = runStart;
      }
    } else {
      runStart = -1;
    }
  }
  if (bestLen < 4) return null;

  // Split at the quietest part of the run, not at its midpoint. A run can spill
  // into a column's ragged right edge, where few rows reach; splitting there
  // would cut a column in half. The bins with the fewest crossings are the
  // gutter proper.
  let quietest = Infinity;
  for (let b = bestStart; b < bestStart + bestLen; b++) quietest = Math.min(quietest, crossings[b]);
  let coreStart = bestStart;
  let coreLen = 0;
  let runFrom = -1;
  for (let b = bestStart; b < bestStart + bestLen; b++) {
    if (crossings[b] === quietest) {
      if (runFrom === -1) runFrom = b;
      if (b - runFrom + 1 > coreLen) {
        coreLen = b - runFrom + 1;
        coreStart = runFrom;
      }
    } else {
      runFrom = -1;
    }
  }
  const splitX = ((coreStart + coreLen / 2) / BINS) * pageWidth;

  let leftChars = 0;
  let rightChars = 0;
  const leftYs = new Set<number>();
  const rightYs = new Set<number>();
  for (const it of items) {
    const chars = it.str.trim().length;
    if (!chars) continue;
    if (it.x + it.width / 2 < splitX) {
      leftChars += chars;
      leftYs.add(Math.round(it.y));
    } else {
      rightChars += chars;
      rightYs.add(Math.round(it.y));
    }
  }
  const total = leftChars + rightChars;
  if (total === 0) return null;
  if (leftChars / total < 0.25 || rightChars / total < 0.25) return null;
  if (leftYs.size < 5 || rightYs.size < 5) return null;
  return splitX;
}

/** Group items into visual lines by baseline, then order each line left to right. */
export function groupLines(items: PositionedItem[]): Line[] {
  if (items.length === 0) return [];

  const heights = items.map((i) => i.height).filter((h) => h > 0).sort((a, b) => a - b);
  const medianHeight = heights.length > 0 ? heights[Math.floor(heights.length / 2)] : 10;
  const groups = groupRows(items);

  const lines: Line[] = [];
  for (const group of groups) {
    group.sort((a, b) => a.x - b.x);
    const fontSize = Math.max(
      medianHeight,
      ...group.map((g) => g.height).filter((h) => h > 0),
      1,
    );
    // A space in a proportional face is roughly a quarter of the point size, so
    // a gap of a fifth is comfortably a real word break while ordinary kerning
    // and italic overhang stay below it.
    const spaceGap = fontSize * 0.2;
    let text = '';
    let cursor = -Infinity;
    for (const item of group) {
      const piece = item.str;
      if (!piece) continue;
      if (text.length > 0) {
        const gap = item.x - cursor;
        const endsSpaced = /\s$/.test(text);
        const startsSpaced = /^\s/.test(piece);
        if (gap > spaceGap && !endsSpaced && !startsSpaced) text += ' ';
      }
      text += piece;
      cursor = item.x + item.width;
    }
    text = text.replace(/\s+/g, ' ').trim();
    if (text) lines.push({ text, y: group[0].y, x: group[0].x });
  }
  return lines;
}

/**
 * Does this row of items run across the gutter rather than sit in one column?
 *
 * A title, an abstract, a spanning figure caption and a footnote rule all do; a
 * row made of one line from each column does not, and the difference is whether
 * the row has a horizontal BREAK at the gutter. Extents are used rather than
 * midpoints on purpose: a single item spanning x=60..530 on a 595pt page has its
 * midpoint on the right of the gutter, so a midpoint test calls it a right-column
 * item and files the whole title inside the right column.
 */
function rowSpansGutter(row: PositionedItem[], splitX: number, pageWidth: number): boolean {
  let hasLeft = false;
  let hasRight = false;
  for (const it of row) {
    if (it.x < splitX) hasLeft = true;
    if (it.x + it.width > splitX) hasRight = true;
  }
  if (!hasLeft || !hasRight) return false;

  // The break has to be wide enough to be a gutter and not merely a word space.
  // findGutter will not return a gutter narrower than 4% of the page, so 3% is
  // below anything it can produce and far above any inter-word gap.
  const minBreak = pageWidth * 0.03;
  const sorted = [...row].sort((a, b) => a.x - b.x);
  let cursor = -Infinity;
  for (const it of sorted) {
    if (cursor <= splitX && it.x >= splitX && it.x - cursor >= minBreak) return false;
    cursor = Math.max(cursor, it.x + it.width);
  }
  return true;
}

/**
 * Assemble one page's items into ordered lines, splitting columns where present.
 *
 * READING ORDER ON A TWO-COLUMN PAGE IS BLOCK-WISE, not simply left-then-right
 * for the whole sheet. Rows that span the gutter cut the page into blocks, each
 * of which is read left column then right column, and the spanning row is
 * emitted in its place between them. That is the order a reader uses: the title
 * block, then the two columns beneath it; the two columns above a full-width
 * figure, then the figure, then the two columns below it.
 *
 * The previous version had no notion of a spanning row at all — it could not,
 * because a page containing one never got a gutter — and it split by item
 * midpoint, which files a full-width title into whichever column its centre
 * happens to fall in.
 */
export function pageLines(items: PositionedItem[], pageWidth: number): Line[] {
  const clean = dropPhantoms(items);
  if (clean.length === 0) return [];
  const gutter = findGutter(clean, pageWidth);
  if (gutter === null) return groupLines(clean);

  const out: Line[] = [];
  let block: PositionedItem[] = [];
  const flush = () => {
    if (block.length === 0) return;
    const left = block.filter((i) => i.x + i.width / 2 < gutter);
    const right = block.filter((i) => i.x + i.width / 2 >= gutter);
    out.push(...groupLines(left), ...groupLines(right));
    block = [];
  };

  for (const row of groupRows(clean)) {
    if (rowSpansGutter(row, gutter, pageWidth)) {
      flush();
      out.push(...groupLines(row));
    } else {
      block.push(...row);
    }
  }
  flush();
  return out;
}

/**
 * The masked form of a line, for spotting running heads.
 *
 * Digits are masked because the folio changes on every page while the rest of
 * the running head does not: "Chapter 3 · 47" and "Chapter 3 · 48" are the same
 * furniture.
 */
export function maskLine(text: string): string {
  return text.toLowerCase().replace(/\d+/g, '#').replace(/\s+/g, ' ').trim();
}

/**
 * Remove repeating running heads and feet.
 *
 * This guard exists because header text is IN the text stream, so a quotation
 * that runs across a page break otherwise has "48 Chapter 3" spliced into its
 * middle and can never match — a genuine verbatim quotation reported as NOT
 * FOUND, which is the worst thing this tool can output.
 *
 * The threshold is deliberately high (60% of pages, minimum three) and the
 * result is reported to the user, because the opposite error — stripping a line
 * that was actually part of the text — manufactures the same false negative from
 * the other direction.
 */
export function stripRunningLines(pages: Line[][]): { pages: Line[][]; dropped: number } {
  if (pages.length < 3) return { pages, dropped: 0 };
  const counts = new Map<string, number>();
  const seenOnPage = (line: Line | undefined) => (line ? maskLine(line.text) : null);

  for (const lines of pages) {
    const forms = new Set<string>();
    const first = seenOnPage(lines[0]);
    const last = seenOnPage(lines[lines.length - 1]);
    if (first) forms.add(first);
    if (last) forms.add(last);
    for (const form of forms) counts.set(form, (counts.get(form) ?? 0) + 1);
  }

  const threshold = Math.max(3, Math.ceil(pages.length * 0.6));
  const furniture = new Set<string>();
  for (const [form, count] of counts) {
    // A masked form of "#" alone is a bare folio and always furniture; longer
    // forms must actually repeat.
    if (count >= threshold && form.length > 0) furniture.add(form);
  }

  let dropped = 0;
  const out = pages.map((lines) => {
    const kept = [...lines];
    while (kept.length > 0 && furniture.has(maskLine(kept[0].text))) {
      kept.shift();
      dropped++;
    }
    while (kept.length > 0 && furniture.has(maskLine(kept[kept.length - 1].text))) {
      kept.pop();
      dropped++;
    }
    return kept;
  });
  return { pages: out, dropped };
}

/**
 * Join assembled lines into one continuous stream, recording where each page
 * starts.
 *
 * Pages are joined into ONE string on purpose. A quotation that runs across a
 * page break is one span of prose, and putting a boundary in the character
 * stream would break it into two partial matches; the boundary lives in the
 * returned offsets instead.
 */
export function joinPages(pages: Line[][]): { text: string; pageStarts: number[] } {
  const flat: { text: string; page: number }[] = [];
  pages.forEach((lines, page) => lines.forEach((line) => flat.push({ text: line.text, page })));

  const out: string[] = [];
  const pageStarts = new Array<number>(pages.length).fill(-1);
  let at = 0;

  for (let i = 0; i < flat.length; i++) {
    const { text, page } = flat[i];
    const previous = i > 0 ? flat[i - 1].text : '';
    // A hyphen at the end of a line whose successor starts LOWER CASE is a word
    // broken by the typesetter: join with no separator and keep the hyphen. A
    // hyphen before a capital is far more often a dash or a compound proper
    // noun, so that line break stays a break.
    const brokenWord = i > 0 && /[\p{L}]-$/u.test(previous) && /^[\p{Ll}]/u.test(text);
    if (i > 0 && !brokenWord) {
      out.push('\n');
      at += 1;
    }
    if (pageStarts[page] === -1) pageStarts[page] = at;
    out.push(text);
    at += text.length;
  }

  // A page that contributed no lines is given the start of the NEXT page that
  // did — a zero-length span at the boundary — so the sheet lookup never falls
  // into a hole and the empty sheet claims no text.
  //
  // BACK-FILLING FROM THE RIGHT IS LOAD-BEARING. It used to back-fill from the
  // LEFT, giving an empty sheet the same start as the sheet before it, and the
  // lookup in match.ts resolves an offset to the HIGHEST sheet whose start is at
  // or below it — so the empty sheet won every tie and swallowed the whole of
  // the previous sheet's text. Measured on a book PDF with a full-page plate at
  // sheet 5: a quotation verbatim on sheet 4, printed page 2, cited "(p. 2)",
  // was reported on sheet 5 / printed page 3 with pinpointMismatch set. The tool
  // showed the "pinpoint differs" chip against a correct citation and sent the
  // author to a blank page. Filling from the right makes the tie resolve to the
  // page that actually holds the text, because that page comes last.
  //
  // Trailing empty sheets get the end of the text, which is past every offset,
  // so they are never selected at all.
  for (let p = pageStarts.length - 1; p >= 0; p--) {
    if (pageStarts[p] === -1) pageStarts[p] = p === pageStarts.length - 1 ? at : pageStarts[p + 1];
  }
  return { text: out.join(''), pageStarts };
}
